import { useEffect, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Button, ButtonToggle, RoundBadge } from '@peacecover/component';
import { ArrowRightSmall, Check } from '@peacecover/icon';
import BettieAgreementSection from '../../../presentation/chat/components/BettieAgreementSection';
import PayLetterPaymentMethodSelector from '../../../presentation/payment/components/PayLetterPaymentMethodSelector';
import {
  useBettieCharge,
  type BettieChargeMode
} from '../../../presentation/chat/hooks/useBettieCharge';
import { useGetMyBettieAutoTopUp } from '../../../domains/bettie/ui/hooks/useGetMyBettieAutoTopup';
import { useChangeAutoTopUpProduct } from '../../../domains/bettie/ui/hooks/useChangeAutoTopUpProduct';
import { BettieGradientColor } from '../../../constants/BettieGradientColor';
import { useWarningToast } from '../../../presentation/hooks/useWarningToast';
import { useCancelAutoTopUp } from '../../../domains/bettie/ui/hooks/useCancelAutoTopUp';
import type { components } from '../../../types/schema';

interface BettieChargeBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** true일 때 1회성 상품(onetime)만 표시 */
  onetimeOnly?: boolean;
  preferOnetimeWhenHasAutoTopup?: boolean;
}

type ApiV1BettieProduct = components['schemas']['ApiV1BettieProduct'];
type ChargeTab = BettieChargeMode;

const formatBettieAmount = (currency: ApiV1BettieProduct['currency'], amount: string) => {
  switch (currency) {
    case 'KRW':
      return `${amount}원`;
    case 'USD':
      return `${amount}$`;
    default:
      return `${amount}원`;
  }
};

const BettieChargeBottomSheet = ({
  isOpen,
  onClose,
  onetimeOnly = false,
  preferOnetimeWhenHasAutoTopup = false
}: BettieChargeBottomSheetProps) => {
  const { colorMode } = useColorMode();
  const { data: myBettieAutoTopup, isSuccess: isAutoTopupQuerySuccess } = useGetMyBettieAutoTopUp();
  const { mutateAsync: changeAutoTopUpProduct, isLoading: isChangingAutoTopUpProduct } =
    useChangeAutoTopUpProduct();
  const { mutateAsync: cancelAutoTopUp } = useCancelAutoTopUp();
  const { oneTimeProducts, selectedItem, handleSelectItem, handlePrimaryClick, isLoadingProducts } =
    useBettieCharge({ onClose });
  const [isPayLetterMethodSelectorOpen, setIsPayLetterMethodSelectorOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ChargeTab>(() =>
    onetimeOnly ? 'onetime' : 'autoTopup'
  );

  const showTabBar = !onetimeOnly && oneTimeProducts.length > 0;

  useEffect(() => {
    if (isLoadingProducts) return;
    if (oneTimeProducts.length === 0) return;
    if (onetimeOnly) {
      setActiveTab('onetime');
      return;
    }
    if (preferOnetimeWhenHasAutoTopup && isAutoTopupQuerySuccess && Boolean(myBettieAutoTopup)) {
      setActiveTab('onetime');
    }
  }, [
    isLoadingProducts,
    onetimeOnly,
    oneTimeProducts.length,
    preferOnetimeWhenHasAutoTopup,
    isAutoTopupQuerySuccess,
    myBettieAutoTopup
  ]);

  const getButtonText = () => {
    if (activeTab === 'autoTopup') {
      return t('BettieChargeBottomSheet.agreeAndSetAutoRefill');
    }

    if (!selectedItem) {
      return t('BettieChargeBottomSheet.goToPayment');
    }

    const amount = Number(selectedItem.baseAmount - selectedItem.discountAmount).toLocaleString();
    const currency = selectedItem.currency === 'USD' ? '$' : '원';

    return t('BettieChargeBottomSheet.agreeAndPay', { price: `${amount}${currency}` });
  };

  const hasAutoTopup = myBettieAutoTopup?.status === 'active';

  const handleChangeTab = (tab: ChargeTab) => {
    setActiveTab(tab);
    handleSelectItem(null);
  };

  const shouldChangeAutoTopupProduct =
    activeTab === 'autoTopup' &&
    myBettieAutoTopup?.autoTopupId != null &&
    selectedItem != null &&
    myBettieAutoTopup?.productId !== selectedItem.bettieProductId;

  const handleProductChange = async () => {
    if (
      shouldChangeAutoTopupProduct &&
      myBettieAutoTopup?.autoTopupId != null &&
      selectedItem != null
    ) {
      const response = await changeAutoTopUpProduct({
        bettieAutoTopUpId: myBettieAutoTopup.autoTopupId,
        productId: selectedItem.bettieProductId
      });
      if (response?.productId) {
        onClose();
      }
      return;
    }

    const needsPayLetterMethod = handlePrimaryClick(undefined, {
      chargeMode: activeTab
    });
    if (needsPayLetterMethod) {
      setIsPayLetterMethodSelectorOpen(true);
    }
  };

  const handleCancelAutoTopUp = async () => {
    try {
      if (hasAutoTopup && myBettieAutoTopup?.autoTopupId != null) {
        const response = await cancelAutoTopUp({
          bettieAutoTopUpId: myBettieAutoTopup.autoTopupId
        });
        if (response?.autoTopupId) {
          onClose();
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content">
      <Sheet.Container className="relative !rounded-t-[20px] !bg-background-light dark:!bg-background-elevated !max-h-[700px] overflow-y-auto">
        <Sheet.Header className="pt-[32px] px-[24px] flex items-center justify-between gap-3">
          <p className="text-[20px] font-bold leading-[24px] text-text-main-lv2 shrink-0">
            {t('BettieChargeBottomSheet.chargeHeartTitle')}
          </p>
        </Sheet.Header>
        <div className="px-[24px] pt-[20px] pb-[10px] flex flex-col">
          <div className="flex flex-col items-center gap-[20px] mb-[28px]">
            {showTabBar && <ChargeTypeTabBar activeTab={activeTab} onChangeTab={handleChangeTab} />}
            {activeTab === 'autoTopup' && oneTimeProducts.length > 0 && (
              <div className="flex flex-col items-center gap-[7px]">
                <p className="text-[16px] font-semibold leading-[19px] text-text-main-lv2">
                  {t('BettieChargeBottomSheet.autoRefillSectionTitle')}
                </p>
                <p className="text-[14px] leading-[17px] text-text-main-lv3">
                  {t('BettieChargeBottomSheet.autoRefillSectionSubtitle')}
                </p>
              </div>
            )}
            <div className="relative flex flex-col gap-[10px] w-full">
              {isLoadingProducts ? (
                activeTab === 'autoTopup' ? (
                  <BettieProductSelectSkeletonGrid />
                ) : (
                  <BettieProductSelectSkeletonList />
                )
              ) : activeTab === 'autoTopup' ? (
                <div className="grid grid-cols-3 gap-[8px]">
                  {oneTimeProducts.map((item) => {
                    const isCurrentAutoTopup =
                      hasAutoTopup &&
                      myBettieAutoTopup?.productId != null &&
                      myBettieAutoTopup.productId === item.bettieProductId;
                    return (
                      <AutoTopupProductCard
                        key={item.bettieProductId}
                        product={item}
                        isSelected={selectedItem?.bettieProductId === item.bettieProductId}
                        isActiveAutoTopupProduct={isCurrentAutoTopup}
                        colorMode={colorMode === 'dark' ? 'dark' : 'light'}
                        onSelect={() => handleSelectItem(item.bettieProductId)}
                      />
                    );
                  })}
                </div>
              ) : (
                oneTimeProducts.map((item) => (
                  <OneTimeProductRow
                    key={item.bettieProductId}
                    product={item}
                    isSelected={selectedItem?.bettieProductId === item.bettieProductId}
                    onSelect={() => handleSelectItem(item.bettieProductId)}
                  />
                ))
              )}
            </div>
          </div>
          <BettieAgreementSection isBilling={activeTab === 'autoTopup'} />
          <div className="bg-background-light dark:bg-background-elevated mt-[18px]">
            <Button
              size="large"
              disabled={
                isLoadingProducts ||
                isChangingAutoTopUpProduct ||
                oneTimeProducts.length === 0 ||
                selectedItem == null
              }
              className="mx-[0px] rounded-[18px] text-[17px] font-semibold w-full"
              onClick={handleProductChange}
            >
              {getButtonText()}
            </Button>
            {hasAutoTopup && (
              <button
                type="button"
                className="mt-[18px] text-[15px] font-semibold leading-[18px] text-text-main-lv3 flex items-center w-full justify-center gap-[4px]"
                onClick={handleCancelAutoTopUp}
              >
                {t('BettieChargeBottomSheet.cancelAutoTopup')}
                <ArrowRightSmall size={20} />
              </button>
            )}
          </div>
        </div>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-[rgba(0,0,0,0.50)]" />
      <PayLetterPaymentMethodSelector
        isOpen={isPayLetterMethodSelectorOpen}
        onClose={() => setIsPayLetterMethodSelectorOpen(false)}
        onSelect={(paymentMethod) => {
          setIsPayLetterMethodSelectorOpen(false);
          handlePrimaryClick(paymentMethod, { chargeMode: activeTab });
        }}
      />
    </Sheet>
  );
};

export default BettieChargeBottomSheet;

const AutoTopupProductCard = ({
  product,
  isSelected,
  isActiveAutoTopupProduct,
  colorMode,
  onSelect
}: {
  product: ApiV1BettieProduct;
  isSelected: boolean;
  isActiveAutoTopupProduct: boolean;
  colorMode: 'light' | 'dark';
  onSelect: () => void;
}) => {
  const showWarningToast = useWarningToast();
  const final = product.baseAmount - product.discountAmount;
  const [, count] = product.name.split(' ');

  const handleSelect = () => {
    if (isActiveAutoTopupProduct) {
      showWarningToast('bottom', t('BettieChargeBottomSheet.warningText') as string);
      return;
    }
    onSelect();
  };

  return (
    <div
      className={
        isSelected
          ? 'rounded-[14px] p-[1px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] bg-gradient-multi-rainbox-lv2'
          : 'rounded-[14px] border border-separator1'
      }
    >
      <button
        type="button"
        onClick={handleSelect}
        className="flex w-full flex-col items-center rounded-[13px] px-[12px] py-[14px] text-center"
        style={
          isSelected
            ? { background: BettieGradientColor.bettieProductFill[colorMode].background }
            : undefined
        }
      >
        <div className="mb-[16px] flex justify-center">
          {isActiveAutoTopupProduct ? (
            <RoundBadge>{t('BettieChargeBottomSheet.activeAutoTopupBadge')}</RoundBadge>
          ) : (
            <Check
              size={20}
              className={isSelected ? undefined : 'text-separator2 dark:text-separator1'}
              gradientStops={
                isSelected
                  ? [
                      { offset: '0', color: '#FF45A9' },
                      { offset: '1', color: '#FF6A6A' }
                    ]
                  : undefined
              }
            />
          )}
        </div>
        <p className="text-[16px] font-semibold leading-[19px] text-text-main-lv2">{count}</p>
        <p className="mt-[2px] text-[10px] leading-[12px] text-text-main-lv4">개당 30원</p>
        <p
          className={`mt-[16px] text-[14px] font-semibold leading-[17px] flex items-center gap-[4px] ${
            isSelected
              ? 'bg-gradient-single-red bg-clip-text text-transparent'
              : 'text-text-primary'
          }`}
        >
          <span className="text-[8px] font-semibold leading-[10px]">{product.currency}</span>
          {formatBettieAmount(product.currency, final.toLocaleString())}
        </p>
      </button>
    </div>
  );
};

const OneTimeProductRow = ({
  product,
  isSelected,
  onSelect
}: {
  product: ApiV1BettieProduct;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { colorMode } = useColorMode();
  const final = product.baseAmount - product.discountAmount;
  const hasDiscount = Number(product.discountAmount) > 0;
  const [title] = product.name.split('/');

  return (
    <div
      className={
        isSelected
          ? 'rounded-[16px] p-[1px] shadow-[0_0_2px_0_rgba(0,0,0,0.04),0_0_8px_0_rgba(0,0,0,0.05)] bg-gradient-multi-rainbox-lv2'
          : 'rounded-[16px] border border-separator1 shadow-[0_0_2px_0_rgba(0,0,0,0.04),0_0_8px_0_rgba(0,0,0,0.05)]'
      }
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full items-center justify-between gap-3 rounded-[15px] bg-background-light px-[16px] py-[20px] text-left dark:bg-background-elevated min-h-[72px]"
        style={
          isSelected
            ? { background: BettieGradientColor.bettieProductFill[colorMode].background }
            : undefined
        }
      >
        <div className="flex items-center gap-[6px]">
          <ButtonToggle type="radio" size="lg" checked={isSelected} />
          <div className="flex items-center gap-[4px]">
            <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv2">
              {title.trim()}
            </p>
            {hasDiscount && <RoundBadge colorVariant="secondary">SALE</RoundBadge>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-[2px]">
          <p
            className={`text-[15px] font-bold leading-[18px] flex items-center gap-[5px] ${
              isSelected
                ? 'bg-gradient-single-red bg-clip-text text-transparent'
                : 'text-text-primary'
            }`}
          >
            <span className="text-[8px] font-semibold leading-[10px]">{product.currency}</span>
            {formatBettieAmount(product.currency, final.toLocaleString())}
          </p>
          {hasDiscount && (
            <p className="text-[12px] font-medium leading-[14px] text-text-sub-lv1 line-through">
              {formatBettieAmount(product.currency, Number(product.baseAmount).toLocaleString())}
            </p>
          )}
        </div>
      </button>
    </div>
  );
};

const ChargeTypeTabBar = ({
  activeTab,
  onChangeTab
}: {
  activeTab: ChargeTab;
  onChangeTab: (tab: ChargeTab) => void;
}) => {
  return (
    <div className="flex w-full rounded-[12px] bg-surface-lv2 p-[4px]">
      <button
        type="button"
        onClick={() => onChangeTab('autoTopup')}
        className={`relative flex min-h-[36px] flex-1 flex-col items-center justify-center rounded-[8px] text-center ${
          activeTab === 'autoTopup'
            ? 'bg-pure-white text-text-main-lv1 dark:bg-backgroundElevated-dark'
            : 'text-text-main-lv3'
        }`}
      >
        <span className="flex flex-wrap items-center justify-center text-[15px] font-semibold leading-[18px]">
          {t('BettieChargeBottomSheet.tabAutoRefill')}
        </span>
        <span className="absolute top-[5px] right-[15px] text-[8px] font-semibold leading-[10px] bg-graident-multi-blue bg-clip-text text-transparent">
          {t('BettieChargeBottomSheet.tabBonusBadge')}
        </span>
      </button>
      <button
        type="button"
        onClick={() => onChangeTab('onetime')}
        className={`flex min-h-[36px] flex-1 items-center justify-center rounded-[8px] text-[15px] font-semibold leading-[18px] ${
          activeTab === 'onetime'
            ? 'bg-pure-white text-text-main-lv1 dark:bg-backgroundElevated-dark'
            : 'text-text-main-lv3'
        }`}
      >
        {t('BettieChargeBottomSheet.tabOneTime')}
      </button>
    </div>
  );
};

const BettieProductSelectSkeletonGrid = () => {
  return (
    <div className="grid grid-cols-3 gap-[8px]">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex flex-col items-center rounded-[14px] border border-[#E5E5EA] px-[8px] pb-[14px] pt-[12px] dark:border-[#3A3A3E] animate-pulse"
        >
          <div className="mb-[6px] h-[24px] w-[24px] rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-[16px] w-[48px] rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="mt-[8px] h-[12px] w-full max-w-[56px] rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="mt-[10px] h-[14px] w-[64px] rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
};

const BettieProductSelectSkeletonList = () => {
  return (
    <div className="flex flex-col gap-[10px]">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-[16px] border border-[#E5E5EA] px-[16px] py-[18px] dark:border-[#3A3A3E] animate-pulse"
        >
          <div className="flex items-center gap-[8px]">
            <div className="h-[24px] w-[24px] rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-[18px] w-[120px] rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
          <div className="h-[18px] w-[72px] rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
};
